/**
 * ★★★ A-7a — A GRADE DIÁRIA DA CLÍNICA, no modelo da planilha do dono.
 *
 * Indicadores na vertical, dias na horizontal, quatro colunas fixas (Indicador, Meta, Total,
 * Média diária). Tudo aqui é **puro**: entra `leads`, `agendamentos` e o documento de metas
 * do mês; sai a grade já montada, linha por linha, pronta para desenhar.
 *
 * ★★★ POR QUE UM MÓDULO NOVO, e não um campo a mais em `consolidarMes`.
 *
 * `consolidarMes` já devolve uma grade por dia — e ela usa réguas que o dono **decidiu não
 * usar aqui**:
 *
 * | linha | `consolidarMes` | ★ a grade da A-7a |
 * |---|---|---|
 * | Agendamentos | só os que passam por `contaNaMeta` (**D2**) | ⚠ **TODOS** os criados no dia — a régua do funil do topo |
 * | Conversão de valor | não existe por dia | `conversaoDeValor` aplicada ao desfecho do dia |
 *
 * ★★ Mexer em `consolidarMes` para atender as duas réguas significaria **mudar número
 * publicado** nas metas individuais e nos Indicadores, que leem o D2 e continuam lendo. ⚠ E o
 * golden `consolidarMes.igualdade.test.js` existe exatamente para impedir que isso aconteça
 * sem decisão. ★ Então a grade nova mora em casa própria, e `consolidarMes` fica intocado.
 *
 * ★★★ AS QUATRO RÉGUAS, ditas por extenso, porque são decisão do dono (13/out/2026):
 *
 *  1. **Contagem pela DATA DO ACONTECIMENTO**, igual ao funil do topo.
 *  2. **Agendamentos: todo agendamento criado no dia** — a D2 **não** entra na grade, e o
 *     funil do topo **não muda**. A D2 continua onde está (metas individuais, Indicadores).
 *  3. **Comparecimento pela data da BAIXA** (`compareceuEm`), pela MESMA função do funil e da
 *     folha (`comparecimentosNoPeriodo`). O rótulo diz "baixas do dia" porque mais da metade
 *     delas é de consulta de outro dia — medido em 13/out: 53 %.
 *  4. **Conversão de valor pela MESMA função** `conversaoDeValor` do cartão do topo, aplicada
 *     aos leads cujo desfecho caiu no dia. ★ É o que faz a coluna "Total" desta linha ser
 *     exatamente o cartão do topo, na mesma tela, sem divergir.
 *
 * ★★ A TAXA DO DIA PODE PASSAR DE 100 %, e não trava — decisão do dono. ⚠ Medido em outubro:
 * já acontece em 1 dos 11 dias vividos. Travar em 100 % faria a grade mentir no primeiro mês.
 */

import {
  METAS_PADRAO, chaveDia, dataFechamento, diasDoMes, limitesDoMes, paraData,
} from '@/metas/calculo';
// ★★ A MESMA função do funil do topo e da folha de pagamento — ver a regra 3 acima.
import { comparecimentosNoPeriodo } from '@/leads/contagemComparecimento';
// ★★ A MESMA função do cartão "Conversão de valor" do topo — ver a regra 4 acima.
import { conversaoDeValor, dataPerda, temDesfechoNegativo } from '@/leads/oportunidade';
import { DIAS_DA_SEMANA } from '@/produtividade/expediente';

/**
 * `metaConversaoValorPct` é campo NOVO do documento do mês, e o padrão dele mora AQUI.
 *
 * ⚠ **NÃO** foi acrescentado a `METAS_PADRAO`: `consolidarMes` devolve `config` inteiro, e uma
 * chave nova ali derrubaria o golden da igualdade por uma mudança que não é de número.
 */
export const METAS_GRADE_PADRAO = { ...METAS_PADRAO, metaConversaoValorPct: 0 };

/** Número ≥ 0 a partir de texto digitado, `null` ou lixo. */
const numero = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/**
 * ★★ Taxa em %, e **`null` quando não há denominador** — nunca 0.
 *
 * ⚠ É a diferença que importa na tela: `pct()` (`calculo.js`) devolve **0** quando o total é
 * 0, e 0 nunca atinge meta nenhuma — então um dia em que não entrou nem saiu nada pintaria
 * **vermelho**. ★ `null` vira "—", sem cor. ⚠ E `pct` **não** é alterada: ela alimenta o
 * funil, os painéis e a pizza.
 */
export function taxaOuNull(parte, total) {
  const t = Number(total);
  if (!Number.isFinite(t) || t <= 0) return null;
  return (Number(parte) || 0) / t * 100;
}

/** Média por dia útil decorrido; `null` quando o mês ainda não começou (nunca `NaN`). */
export function mediaOuNull(total, uteisDecorridos) {
  const n = Number(uteisDecorridos);
  if (!Number.isFinite(n) || n <= 0) return null;
  return (Number(total) || 0) / n;
}

/** `'AAAA-MM-DD'` → `Date` ao MEIO-DIA local (a armadilha de fuso da VC). */
function dataDaChave(chave) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(chave ?? ''));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
}

/**
 * ★★★ O DIA ÚTIL QUE ABSORVE UM ACONTECIMENTO — decisão do dono.
 *
 * Sábado, domingo e feriado **não viram coluna** (desenho aprovado). O que acontece neles
 * soma no **dia útil seguinte**. ⚠ Medido em outubro/2026: a regra move **47 leads** e mais
 * nada — ninguém agenda, dá baixa, fecha ou orça no domingo.
 *
 * ⚠⚠⚠ O CASO DE BORDA QUE FAZ DINHEIRO DESAPARECER: um acontecimento no fim do mês cujo
 * próximo dia útil cai no **mês seguinte** (31/jan/2027 é domingo). ★ Ele volta para o
 * **ÚLTIMO dia útil do mês**. ⚠ A alternativa — empurrar para o mês seguinte — faria a soma
 * das colunas deixar de ser o total do mês, e uma venda sairia da grade sem ninguém ver.
 *
 * @param feriados lista de `'AAAA-MM-DD'`. ★ Desde 02/11/2026 vem de `feriadosDaClinica` — os
 *   mesmos feriados dos Indicadores (D7).
 * @returns a chave do dia útil, ou `null` quando a clínica não tem nenhum dia útil.
 */
export function diaUtilDe(chave, diasSemana = METAS_PADRAO.diasUteis, feriados = []) {
  const semana = Array.isArray(diasSemana) ? diasSemana : [];
  if (semana.length === 0) return null;
  const base = dataDaChave(chave);
  if (!base) return null;
  const fer = new Set(feriados ?? []);
  const util = (d) => semana.includes(d.getDay()) && !fer.has(chaveDia(d));
  const mes = chaveDia(base).slice(0, 7);

  for (const d = new Date(base); chaveDia(d).slice(0, 7) === mes; d.setDate(d.getDate() + 1)) {
    if (util(d)) return chaveDia(d);
  }
  // Não há dia útil adiante NESTE mês: o último dia útil do mês absorve.
  for (const d = new Date(base); chaveDia(d).slice(0, 7) === mes; d.setDate(d.getDate() - 1)) {
    if (util(d)) return chaveDia(d);
  }
  return null;
}

/** Os dias da semana (0 = domingo) em que o expediente tem faixa — a régua do Dashboard. */
export function diasDaSemanaDoExpediente(expediente) {
  return DIAS_DA_SEMANA.map((_, i) => i).filter((i) => {
    const faixa = expediente?.[DIAS_DA_SEMANA[i]];
    return Boolean(faixa?.de && faixa?.ate);
  });
}

/**
 * ★★★ O FUNIL INVERTIDO — de onde sai a meta de LEADS, que não é digitada.
 *
 *   fechamentos   = metaValorVendas ÷ ticketMedioAlvo
 *   comparecimentos = fechamentos   ÷ (metaFechamentoPct    ÷ 100)
 *   agendamentos  = comparecimentos ÷ (metaComparecimentoPct ÷ 100)
 *   leadsMes      = agendamentos    ÷ (metaAgendamentoPct   ÷ 100)
 *   leadsDia      = ceil(leadsMes ÷ totalDiasUteis)
 *
 * ★★ A REGRA É "`null`, NUNCA ZERO": qualquer divisor ≤ 0, vazio ou não-numérico — e também
 * `metaValorVendas ≤ 0` — devolve `null` em tudo. ⚠⚠⚠ Sem a guarda do VALOR, `0 ÷ qualquer`
 * daria 0, e **todo dia ficaria verde** por ter entrado ≥ 0 lead: o pior falso positivo
 * possível. ★ Dito de frente, porque é decisão e não detalhe: **meta 0 não é meta.**
 *
 * ⚠ Em produção (medido em 13/out) `metaValorVendas` e `ticketMedioAlvo` estão em **zero** —
 * então a linha "Leads" sobe mostrando "—" em todos os dias até o dono digitar os dois.
 */
export function funilInvertido({
  metaValorVendas, ticketMedioAlvo,
  metaFechamentoPct, metaComparecimentoPct, metaAgendamentoPct,
  totalDiasUteis,
} = {}) {
  const vazio = {
    fechamentos: null, comparecimentos: null, agendamentos: null, leadsMes: null, leadsDia: null,
  };
  const vendas = numero(metaValorVendas);
  const ticket = numero(ticketMedioAlvo);
  const fech = numero(metaFechamentoPct);
  const comp = numero(metaComparecimentoPct);
  const agend = numero(metaAgendamentoPct);
  const uteis = Number(totalDiasUteis);
  if (vendas <= 0 || ticket <= 0 || fech <= 0 || comp <= 0 || agend <= 0) return vazio;
  if (!Number.isFinite(uteis) || uteis <= 0) return vazio;

  const fechamentos = vendas / ticket;
  const comparecimentos = fechamentos / (fech / 100);
  const agendamentos = comparecimentos / (comp / 100);
  const leads = agendamentos / (agend / 100);
  return {
    fechamentos,
    comparecimentos,
    agendamentos,
    leadsMes: Math.ceil(leads),
    leadsDia: Math.ceil(leads / uteis),
  };
}

/** `valor >= meta`, ou `null` quando não há meta (a célula fica sem cor). */
const atingiu = (valor, meta) => {
  if (meta === null || meta === undefined || !(Number(meta) > 0)) return null;
  if (valor === null || valor === undefined) return null;
  return Number(valor) >= Number(meta);
};

/**
 * Monta a grade do mês.
 *
 * ⚠ `agora` entra por PARÂMETRO (regra da WN): "hoje" e "dia futuro" dependem do relógio, e
 * função que o lê por dentro não é testável em instante nenhum.
 *
 * @param mes          'AAAA-MM'
 * @param leads        todos os leads da clínica (os que o `LeadsProvider` já tem em memória)
 * @param agendamentos a coleção inteira (o Dashboard já assina sem janela)
 * @param metas        o documento `metas/{AAAA-MM}`, ou `{}`
 * @param feriados     ★ 02/11/2026 — `feriadosDaClinica(config)`: o feriado não vira coluna e
 *                     sai da divisão da meta de valor
 * @param expediente   ★ 02/11/2026 — `expedienteDaClinica(config)`. Quando vem, os dias úteis
 *                     saem DELE (dia da semana com faixa), e `metas.diasUteis` deixa de valer.
 */
export function montarGradeDiaria({
  mes,
  leads = [],
  agendamentos = [],
  metas = {},
  feriados = [],
  expediente = null,
  agora = new Date(),
}) {
  const config = { ...METAS_GRADE_PADRAO, ...metas };
  /**
   * ★★ 02/11/2026 (D4) — OS DIAS ÚTEIS SAEM DO EXPEDIENTE DA CLÍNICA, a mesma régua dos
   * Indicadores. ⚠ Setembro e outubro gravaram `diasUteis: [1..6]`; novembro não gravou e caía
   * em `[1..5]` — o sábado sumia do Dashboard de um mês para o outro. O expediente padrão dá
   * `[1..6]`: setembro e outubro ficam idênticos, e novembro passa a contar o sábado.
   * ★ No Dashboard o sábado vale um dia INTEIRO na divisão da meta de valor (P3).
   * ⚠ Sem `expediente` (chamada antiga), vale o que valia: `metas.diasUteis ?? [1..5]`.
   */
  const diasSemana = expediente
    ? diasDaSemanaDoExpediente(expediente)
    : Array.isArray(config.diasUteis) ? config.diasUteis : METAS_PADRAO.diasUteis;
  const fer = (feriados ?? []).filter((f) => String(f).startsWith(`${mes}-`));

  const todosOsDias = diasDoMes(mes, diasSemana);
  const uteis = todosOsDias.filter((d) => d.util && !fer.includes(d.chave));
  const totalDiasUteis = uteis.length;
  const chaveHoje = chaveDia(agora);
  const uteisDecorridos = uteis.filter((d) => d.chave <= chaveHoje).length;

  const { inicio, fim } = limitesDoMes(mes);
  const dentro = (data) => data && data >= inicio && data <= fim;

  // ---- os recortes do mês: as MESMAS regras do funil do topo (escopo "tudo") ----
  const leadsCriados = leads.filter((l) => dentro(paraData(l.criadoEm)));
  /**
   * ⚠⚠ **SEM `contaNaMeta`** — decisão do dono: a grade conta TODO agendamento criado no dia,
   * igual ao primeiro degrau do funil. ★ A D2 continua valendo onde já estava.
   */
  const marcados = agendamentos.filter((a) => dentro(paraData(a.criadoEm)));
  const compareceram = comparecimentosNoPeriodo(leads, agendamentos, dentro);
  const fechados = leads.filter((l) => l.etapa === 'fechado' && dentro(dataFechamento(l)));
  const perdidos = leads.filter((l) => temDesfechoNegativo(l) && dentro(dataPerda(l)));

  const totais = {
    leads: leadsCriados.length,
    agendados: marcados.length,
    comparecimentos: compareceram.length,
    fechamentos: fechados.length,
    valor: fechados.reduce((t, l) => t + (Number(l.valorFechado) || 0), 0),
  };
  const conversaoMes = conversaoDeValor([...fechados, ...perdidos]);

  // ---- distribuição por dia ÚTIL (sábado, domingo e feriado somam no seguinte) ----
  const vazio = () => ({
    leads: 0, agendados: 0, comparecimentos: 0, fechamentos: 0, valor: 0, comDesfecho: [],
  });
  const porDia = new Map(uteis.map((d) => [d.chave, vazio()]));
  const semDiasUteis = totalDiasUteis === 0;
  const alvo = (data) => {
    const chave = chaveDia(data);
    if (!chave) return null;
    return porDia.get(diaUtilDe(chave, diasSemana, fer)) ?? null;
  };
  const acumular = (data, campo, quanto = 1) => {
    const linha = alvo(data);
    if (linha) linha[campo] += quanto;
  };

  for (const l of leadsCriados) acumular(l.criadoEm, 'leads');
  for (const a of marcados) acumular(a.criadoEm, 'agendados');
  // ★ `quando` é a data da BAIXA (`compareceuEm`) — a regra única da WK.
  for (const { quando } of compareceram) acumular(quando, 'comparecimentos');
  for (const l of fechados) {
    acumular(dataFechamento(l), 'fechamentos');
    acumular(dataFechamento(l), 'valor', Number(l.valorFechado) || 0);
  }
  // ★ O desfecho posiciona o lead na conversão do dia: fechado por `dataFechamento`,
  //   perdido por `dataPerda` — o MESMO par que a função do cartão do topo usa.
  for (const l of fechados) { const d = alvo(dataFechamento(l)); if (d) d.comDesfecho.push(l); }
  for (const l of perdidos) { const d = alvo(dataPerda(l)); if (d) d.comDesfecho.push(l); }

  const dias = uteis.map((d) => ({
    chave: d.chave,
    data: d.data,
    diaSemana: d.diaSemana,
    hoje: d.chave === chaveHoje,
    futuro: d.chave > chaveHoje,
  }));

  const funil = funilInvertido({ ...config, totalDiasUteis });
  const metaValorDia = numero(config.metaValorVendas) > 0 && totalDiasUteis > 0
    ? numero(config.metaValorVendas) / totalDiasUteis
    : null;
  const metaTicket = numero(config.ticketMedioAlvo) > 0 ? numero(config.ticketMedioAlvo) : null;
  const dado = (chave) => porDia.get(chave) ?? vazio();
  const conversaoDoDia = (chave) => conversaoDeValor(dado(chave).comDesfecho);

  /**
   * ★★ AS SETE LINHAS. `quantidade` em cada célula é a **parcela aditiva** daquela linha, e
   * `quantidadeTotal` é o total do mês da MESMA grandeza — é o par que o invariante
   * "soma das colunas = Total" compara, nas sete linhas, sem exceção.
   *
   * ⚠ Nas duas linhas derivadas (Ticket médio e Conversão de valor) o "Total" na tela é uma
   * RAZÃO, que não é a soma das razões dos dias. ★ A grandeza aditiva delas é o **dinheiro**
   * (valor vendido / valor fechado com desfecho), e é ela que o invariante confere.
   */
  const linhas = [
    {
      id: 'leads',
      rotulo: 'Leads que entraram',
      nota: 'pela data de entrada do lead',
      formato: 'inteiro',
      meta: { tipo: 'auto', valor: funil.leadsDia },
      total: totais.leads,
      quantidadeTotal: totais.leads,
      media: mediaOuNull(totais.leads, uteisDecorridos),
      celulas: dias.map((d) => {
        const valor = dado(d.chave).leads;
        return {
          chave: d.chave, quantidade: valor, valor, taxa: null,
          atingiu: d.futuro ? null : atingiu(valor, funil.leadsDia),
        };
      }),
    },
    {
      id: 'agendados',
      rotulo: 'Agendamentos',
      nota: 'agendados ÷ leads do dia · todo agendamento criado no dia',
      formato: 'inteiro',
      meta: { tipo: 'pct', campo: 'metaAgendamentoPct', valor: numero(config.metaAgendamentoPct) },
      total: totais.agendados,
      quantidadeTotal: totais.agendados,
      totalTaxa: taxaOuNull(totais.agendados, totais.leads),
      media: mediaOuNull(totais.agendados, uteisDecorridos),
      celulas: dias.map((d) => {
        const linha = dado(d.chave);
        const taxa = taxaOuNull(linha.agendados, linha.leads);
        return {
          chave: d.chave, quantidade: linha.agendados, valor: linha.agendados, taxa,
          atingiu: d.futuro ? null : atingiu(taxa, numero(config.metaAgendamentoPct)),
        };
      }),
    },
    {
      id: 'comparecimentos',
      // ★ O rótulo DIZ o relógio: 53 % das baixas são de consulta de outro dia (medido 13/out).
      rotulo: 'Comparecimento (baixas do dia)',
      nota: 'baixas registradas ÷ agendados do dia',
      formato: 'inteiro',
      meta: { tipo: 'pct', campo: 'metaComparecimentoPct', valor: numero(config.metaComparecimentoPct) },
      total: totais.comparecimentos,
      quantidadeTotal: totais.comparecimentos,
      totalTaxa: taxaOuNull(totais.comparecimentos, totais.agendados),
      media: mediaOuNull(totais.comparecimentos, uteisDecorridos),
      celulas: dias.map((d) => {
        const linha = dado(d.chave);
        const taxa = taxaOuNull(linha.comparecimentos, linha.agendados);
        return {
          chave: d.chave, quantidade: linha.comparecimentos, valor: linha.comparecimentos, taxa,
          atingiu: d.futuro ? null : atingiu(taxa, numero(config.metaComparecimentoPct)),
        };
      }),
    },
    {
      id: 'fechamentos',
      rotulo: 'Fechamento',
      nota: 'fechamentos ÷ comparecimentos do dia',
      formato: 'inteiro',
      meta: { tipo: 'pct', campo: 'metaFechamentoPct', valor: numero(config.metaFechamentoPct) },
      total: totais.fechamentos,
      quantidadeTotal: totais.fechamentos,
      totalTaxa: taxaOuNull(totais.fechamentos, totais.comparecimentos),
      media: mediaOuNull(totais.fechamentos, uteisDecorridos),
      celulas: dias.map((d) => {
        const linha = dado(d.chave);
        const taxa = taxaOuNull(linha.fechamentos, linha.comparecimentos);
        return {
          chave: d.chave, quantidade: linha.fechamentos, valor: linha.fechamentos, taxa,
          atingiu: d.futuro ? null : atingiu(taxa, numero(config.metaFechamentoPct)),
        };
      }),
    },
    {
      id: 'valor',
      /**
       * ★★★ A-7c — O RÓTULO DIZ A UNIDADE DA CÉLULA, e isso é o que torna o número curto
       * honesto: a célula do dia mostra `18,5`, não `R$ 18.500`.
       *
       * ⚠⚠ Medido em Chrome a 1366 px: com `R$ 123.456` na célula a coluna de dia vai a
       * 106–110 px e **cabem 4 dias**; com `18,5` ela fica em 64 px e **cabem 9**. ★ Estreitar
       * as quatro colunas fixas, sozinho, rendia **um** dia.
       * ★★ "Total" e "Média diária" continuam em **reais cheios** (decisão do dono) — elas
       * estão no bloco fixo, que tem lugar.
       */
      rotulo: 'Valor de vendas (R$ mil)',
      nota: metaValorDia === null
        ? 'meta do mês ÷ dias úteis'
        : 'meta do mês ÷ dias úteis = meta do dia',
      formato: 'moeda',
      formatoCelula: 'milhares',
      meta: { tipo: 'moeda', campo: 'metaValorVendas', valor: numero(config.metaValorVendas), porDia: metaValorDia },
      total: totais.valor,
      quantidadeTotal: totais.valor,
      media: mediaOuNull(totais.valor, uteisDecorridos),
      celulas: dias.map((d) => {
        const valor = dado(d.chave).valor;
        return {
          chave: d.chave, quantidade: valor, valor, taxa: null,
          atingiu: d.futuro ? null : atingiu(valor, metaValorDia),
        };
      }),
    },
    {
      id: 'ticket',
      /**
       * ⚠⚠ O "(R$ mil)" vale para ESTA linha também, e é o que entrega os 9 dias medidos: o
       * ganho de +5 saiu da medição com **as duas** linhas de dinheiro encurtadas. ★ Com o
       * ticket em reais cheios a coluna volta a ~106 px e o ganho cai para +1.
       */
      rotulo: 'Ticket médio (R$ mil)',
      nota: 'valor vendido ÷ fechamentos do dia',
      formato: 'moeda',
      formatoCelula: 'milhares',
      meta: { tipo: 'moeda', campo: 'ticketMedioAlvo', valor: numero(config.ticketMedioAlvo) },
      // ★ Total é RAZÃO; a grandeza aditiva é o dinheiro (ver o comentário do bloco).
      total: totais.fechamentos > 0 ? totais.valor / totais.fechamentos : null,
      quantidadeTotal: totais.valor,
      // ⚠ "Média diária" de uma razão não significa nada — o desenho manda "—".
      media: null,
      celulas: dias.map((d) => {
        const linha = dado(d.chave);
        const valor = linha.fechamentos > 0 ? linha.valor / linha.fechamentos : null;
        return {
          chave: d.chave, quantidade: linha.valor, valor, taxa: null,
          atingiu: d.futuro ? null : atingiu(valor, metaTicket),
        };
      }),
    },
    {
      id: 'conversao',
      rotulo: 'Conversão de valor',
      nota: 'fechado ÷ orçado de quem teve desfecho no dia',
      formato: 'pct',
      meta: { tipo: 'pct', campo: 'metaConversaoValorPct', valor: numero(config.metaConversaoValorPct) },
      total: conversaoMes.taxa,
      quantidadeTotal: conversaoMes.fechado,
      media: null,
      celulas: dias.map((d) => {
        const c = conversaoDoDia(d.chave);
        return {
          chave: d.chave, quantidade: c.fechado, valor: c.taxa, taxa: c.taxa,
          atingiu: d.futuro ? null : atingiu(c.taxa, numero(config.metaConversaoValorPct)),
        };
      }),
    },
  ];

  return {
    mes,
    config,
    dias,
    totalDiasUteis,
    uteisDecorridos,
    semDiasUteis,
    totais,
    conversaoMes,
    funil,
    metaValorDia,
    linhas,
  };
}
